/**
 * לינק הזמנה לסוכני BASIC / PRO, שיוך נרשם, וזיכוי חודשי קבוע
 * אחרי שהסוכן המוזמן עובר מחבילת הניסיון לחבילה בתשלום.
 *
 * הזיכוי הוא אחוז קבוע ממחיר הברוטו החודשי של החבילה ששולמה,
 * לפי חבילת הסוכן המזמין (BASIC 10%, PRO 20%), כל עוד המנוי בתשלום.
 */

import {
  AGENT_TRIAL_DAYS,
  REGISTRATION_PACKAGE_CODES,
  REGISTRATION_PACKAGE_NET_AMOUNTS,
} from "./agentRegistrationPlans";
import { resolvePackagePriceWithVat } from "./resolvePackagePriceWithVat";
import { formatMonthlyGrossAmountIls } from "./subscriptionBillingPolicySsot";

export const AGENT_REFERRAL_QUERY_PARAM = "ref";

export const REFERRAL_LINK_PACKAGE_CODES = [
  REGISTRATION_PACKAGE_CODES.BASIC,
  REGISTRATION_PACKAGE_CODES.PRO,
];

/** אחוז קבוע ממחיר הברוטו של החבילה ששילם הסוכן המוזמן. */
export const REFERRAL_STANDING_CREDIT_RATE_BY_REFERRER = {
  [REGISTRATION_PACKAGE_CODES.BASIC]: 0.1,
  [REGISTRATION_PACKAGE_CODES.PRO]: 0.2,
};

const PAID_PACKAGE_CODES = [
  REGISTRATION_PACKAGE_CODES.BASIC,
  REGISTRATION_PACKAGE_CODES.PRO,
  REGISTRATION_PACKAGE_CODES.AGENCY,
];

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

export function normalizeReferralPackageCode(code) {
  const raw = String(code || "").trim().toUpperCase();
  if (raw === "STARTER") return REGISTRATION_PACKAGE_CODES.BASIC;
  return raw;
}

export function canOfferAgentReferralLink(packageCode) {
  const code = normalizeReferralPackageCode(packageCode);
  return REFERRAL_LINK_PACKAGE_CODES.includes(code);
}

export function resolveAgentReferralCode(agent) {
  if (!agent || typeof agent !== "object") return "";
  const code = agent.agent_code ?? agent.agentCode ?? agent.code;
  if (code != null && String(code).trim()) return String(code).trim();
  if (agent.id != null && String(agent.id).trim()) return String(agent.id).trim();
  return "";
}

export function buildAgentReferralRegistrationPath(agentCode) {
  const code = String(agentCode || "").trim();
  if (!code) return "";
  return `/registeragent?${AGENT_REFERRAL_QUERY_PARAM}=${encodeURIComponent(code)}`;
}

export function buildAgentReferralRegistrationUrl(agentCode, origin) {
  const path = buildAgentReferralRegistrationPath(agentCode);
  if (!path) return "";
  const base = String(origin || "").replace(/\/$/, "");
  return base ? `${base}${path}` : path;
}

export function readReferralCodeFromSearch(search) {
  const raw = search == null ? "" : String(search);
  const query = raw.startsWith("?") ? raw : raw ? `?${raw}` : "";
  try {
    return String(new URLSearchParams(query).get(AGENT_REFERRAL_QUERY_PARAM) || "").trim();
  } catch {
    return "";
  }
}

/**
 * @param {string} refRaw
 * @param {Array<{ value: unknown, agentCodeRaw?: string, label?: string }>} options
 */
export function matchReferrerOption(refRaw, options) {
  const ref = String(refRaw || "").trim();
  if (!ref) return null;
  const list = Array.isArray(options) ? options : [];
  const lower = ref.toLowerCase();
  return (
    list.find((option) => {
      const id = String(option?.value ?? "").trim();
      const code = String(option?.agentCodeRaw ?? "").trim();
      return id === ref || (code && code.toLowerCase() === lower);
    }) || null
  );
}

/** Empty is valid; non-empty must match an agent id or agent_code privately. */
export function isReferrerCodeInputValid(refRaw, options) {
  const raw = String(refRaw || "").trim();
  if (!raw) return true;
  return Boolean(matchReferrerOption(raw, options));
}

export const REFERRER_CODE_INVALID_MESSAGE = "קוד סוכן לא נמצא";

export function buildRegistrationReferrerFields(recommendedBy, options) {
  const raw = recommendedBy == null ? "" : String(recommendedBy).trim();
  if (!raw) {
    return {
      recommendedBy: null,
      referrerAgentId: null,
      referrerCode: null,
    };
  }
  const match = matchReferrerOption(raw, options);
  if (!match) {
    return {
      recommendedBy: null,
      referrerAgentId: null,
      referrerCode: null,
    };
  }
  const agentId = match.value;
  const codeRaw = match.agentCodeRaw != null ? String(match.agentCodeRaw).trim() : "";
  return {
    recommendedBy: agentId,
    referrerAgentId: agentId,
    referrerCode: codeRaw || String(agentId),
  };
}

/**
 * זיכוי חודשי קבוע לסוכן המזמין, מרגע שהמוזמן משלם על חבילה.
 * @returns {null | {
 *   referrerPackageCode: string,
 *   referredPaidPackageCode: string,
 *   rate: number,
 *   monthlyGrossCreditIls: number,
 *   kind: "standing_monthly",
 *   whileReferredSubscriptionPaid: true,
 * }}
 */
export function resolveReferralStandingCredit({
  referrerPackageCode,
  referredPaidPackageCode,
} = {}) {
  const referrer = normalizeReferralPackageCode(referrerPackageCode);
  const referred = normalizeReferralPackageCode(referredPaidPackageCode);
  const rate = REFERRAL_STANDING_CREDIT_RATE_BY_REFERRER[referrer];
  if (rate == null || !PAID_PACKAGE_CODES.includes(referred)) return null;
  const net = REGISTRATION_PACKAGE_NET_AMOUNTS[referred];
  const { grossAmount } = resolvePackagePriceWithVat(net);
  if (grossAmount == null) return null;
  return {
    referrerPackageCode: referrer,
    referredPaidPackageCode: referred,
    rate,
    monthlyGrossCreditIls: roundMoney(grossAmount * rate),
    kind: "standing_monthly",
    whileReferredSubscriptionPaid: true,
    trialDaysBeforePayment: AGENT_TRIAL_DAYS,
  };
}

export function listReferralStandingCreditsForReferrer(referrerPackageCode) {
  return PAID_PACKAGE_CODES.map((code) =>
    resolveReferralStandingCredit({
      referrerPackageCode,
      referredPaidPackageCode: code,
    }),
  ).filter(Boolean);
}

export function formatReferralStandingCreditLine(credit) {
  if (!credit) return "";
  const amount = formatMonthlyGrossAmountIls(credit.monthlyGrossCreditIls);
  const percent = Math.round(credit.rate * 100);
  return `${credit.referredPaidPackageCode}: ${amount} לחודש (${percent}%)`;
}
